import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle2, Paperclip, Receipt, Upload, X } from 'lucide-react'
import { getVendors } from '../../api/master'
import { getExpenseSetup, postSGA, uploadExpenseFiles } from '../../api/operatingCosts'
import ErrorBanner from '../../components/ErrorBanner'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { useSession } from '../../session'
import { money, num } from '../sales/salesShared'
import { FILE_ACCEPT, checkFiles, fileSize } from './ExpenseFiles'
import { groupAccounts } from './expensesShared'
import JobSelect from '../../components/JobSelect'
import { CreateOption } from '../../components/CreateOption'

// Record an indirect cost of running the business: rent, power, fuel,
// repairs... The expense account is debited and the account it was paid
// from is credited, both chosen from the chart of accounts. Credited to
// accounts payable, it's on account: the vendor gets a bill that's paid
// later under Purchase. Costs of the goods and services sold go through
// Purchase instead. Without Accounting in the plan no accounts are shown:
// the expense goes to the default expense account (Transaction accounts),
// and Paid by (cash, transfer, card, or not paid yet) picks the account it
// was paid from, as on the customer payments page. The books still post
// the same entry quietly.
export default function ExpenseForm() {
  const nav = useNavigate()
  const { roles, has } = useSession()
  const isAdmin = roles.includes('admin')
  const books = has('accounting')
  const seesBooks = (isAdmin || roles.includes('accounting')) && books
  const payPage = (isAdmin || roles.includes('purchase')) && has('purchase') ? '/purchase/ap-ledger' : ((roles.includes('admin') || roles.includes('accounting')) && has('accounting') ? '/accounting/ap/payment' : '')
  const [setup, setSetup] = useState(null)
  const [vendors, setVendors] = useState([])
  const [debit, setDebit] = useState('')
  const [credit, setCredit] = useState('')
  const [method, setMethod] = useState('Cash')  // Paid by, without Accounting
  const [amount, setAmount] = useState('')
  const [date, setDate] = useState(localToday())
  const [what, setWhat] = useState('')
  const [linkParams] = useSearchParams()
  const [jobId, setJobId] = useState(linkParams.get('job') || '')
  const [vendorId, setVendorId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [billNumber, setBillNumber] = useState('')
  const [files, setFiles] = useState([])
  const fileInput = useRef(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)

  useEffect(() => {
    getExpenseSetup().then((s) => {
      setSetup(s)
      setDebit(s?.default_debit || '')
      setCredit(s?.default_credit || '')
    }).catch((e) => setError(e.message || 'Could not load the form.'))
    getVendors().then((vs) => setVendors(vs || [])).catch(() => {})
  }, [])

  const debitAccounts = setup?.debit_accounts || []
  // Owing a vendor needs somewhere to pay the bill later: Purchase or Accounting.
  const canOwe = has('purchase') || has('accounting')
  const creditAccounts = (setup?.credit_accounts || []).filter((a) => canOwe || !a.owed)
  // Without Accounting: Paid by picks the account; the expense account is the default.
  const paidBy = setup?.paid_by || {}
  const owedOK = canOwe && !!paidBy.owed
  const creditCode = books ? credit : (method === 'Not paid yet' ? paidBy.owed : method === 'Cash' ? paidBy.cash : paidBy.bank) || setup?.default_credit
  const debitAcc = debitAccounts.find((a) => a.code === (books ? debit : setup?.default_debit))
  const creditAcc = creditAccounts.find((a) => a.code === creditCode)
  const onAccount = !!creditAcc?.owed
  const vendor = vendors.find((v) => String(v.vendor_id) === String(vendorId))

  const save = async () => {
    setError('')
    if (!debitAcc) { setError(books ? 'Choose the expense account to debit.' : 'Expenses aren’t set up yet. Ask ASYS for help.'); return }
    if (!creditAcc) { setError(books ? 'Choose the account it was paid from.' : 'Choose how it was paid.'); return }
    if (!(num(amount) > 0)) { setError('Enter how much was spent.'); return }
    if (date > localToday()) { setError('The date can’t be in the future.'); return }
    if (onAccount && !vendor) { setError('Choose the vendor this is owed to.'); return }
    if (onAccount && dueDate && dueDate < date) { setError('The due date can’t be before the expense date.'); return }
    setSaving(true)
    try {
      const res = await postSGA({
        date,
        expense_code: debitAcc.code,
        paid_from_code: creditAcc.code,
        amount: num(amount),
        memo: what.trim() || (books ? debitAcc.name : 'Expense'),
        vendor_id: vendorId ? Number(vendorId) : null,
        job_id: jobId ? Number(jobId) : null,
        ...(onAccount ? { due_date: dueDate || date, bill_number: billNumber.trim() } : {}),
      })
      // The expense is saved; the files go up next. If they can't, it says
      // so and they can be attached from the expense afterwards.
      let attached = 0
      let fileError = ''
      if (files.length) {
        try {
          attached = (await uploadExpenseFiles(res.je_id, files)).length
        } catch (e) {
          fileError = e.message || 'The files could not be attached.'
        }
      }
      setDone({ ...res, amount: num(amount), what: what.trim(), debit: debitAcc, credit: creditAcc, method, vendor, due: dueDate || date, attached, fileError })
    } catch (e) {
      setError(e.message || 'Could not record the expense.')
    } finally {
      setSaving(false)
    }
  }

  const again = () => { setDone(null); setAmount(''); setWhat(''); setVendorId(''); setDueDate(''); setBillNumber(''); setFiles([]) }

  const pickFiles = (list) => {
    const chosen = [...(list || [])]
    if (fileInput.current) fileInput.current.value = ''
    const problem = checkFiles(chosen)
    if (problem) { setError(problem); return }
    setError('')
    setFiles((fs) => [...fs, ...chosen].slice(0, 10))
  }

  if (done) {
    return (
      <div className="ci-form-page">
        <DocHeader back={<BackLink to="/operating" label="Expenses" />} title="Expense recorded" />
        <section className="doc-card doc-done">
          <CheckCircle2 size={28} aria-hidden="true" />
          <div>
            <h2>{money(done.amount)} · {done.what || (books ? done.debit.name : 'Expense')}</h2>
            <p>
              {books
                ? <>Debited {done.debit.code} {done.debit.name}, credited {done.credit.code} {done.credit.name}.</>
                : <>{done.credit.owed ? 'Not paid yet.' : `Paid by ${done.method.toLowerCase()}.`}</>}
              {seesBooks && done.je_id && <> Journal entry <Link to={`/accounting/journal/${done.je_id}`}>{docNo('je', done.je_no)}</Link>.</>}
            </p>
            {done.attached > 0 && <p><Paperclip size={13} aria-hidden="true" /> {done.attached} {done.attached === 1 ? 'file' : 'files'} attached.</p>}
            {done.fileError && (
              <p className="exp-file-error">
                The expense is saved, but the files weren’t attached: {done.fileError} <Link to={`/operating/expenses/${done.je_id}`}>Attach them on the expense</Link>.
              </p>
            )}
            {done.bill_id && (
              <p>
                Owed to {done.vendor?.name || 'the vendor'} on bill {docNo('bill', done.bill_no)}, due {fmtDay(done.due)}.
                {payPage && <> <Link to={`${payPage}?vendor_id=${done.vendor?.vendor_id}`}>Pay it</Link> when you do.</>}
              </p>
            )}
          </div>
          <div className="doc-done-actions">
            <button type="button" className="ci-btn ci-btn-ghost" onClick={again}>Record another</button>
            <Link className="ci-btn ci-btn-ghost" to={`/operating/expenses/${done.je_id}`}>Open the expense</Link>
            <Link className="ci-btn ci-btn-primary" to="/operating/expenses">See expenses</Link>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/operating" label="Expenses" />} title="Record an expense" meta="An indirect cost of running the business, such as rent, power, fuel or repairs." />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card exp-form">
        {books ? (
          <div className="ci-form-meta-top two">
            <label className="ci-form-field">
              <span>Expense account (debit)</span>
              <AccountSelect accounts={debitAccounts} value={debit} onChange={setDebit} empty="No expense accounts" />
            </label>
            <label className="ci-form-field">
              <span>Paid from (credit)</span>
              <AccountSelect accounts={creditAccounts} value={credit} onChange={setCredit} empty="No accounts to pay from" />
            </label>
          </div>
        ) : (
          <div className="ci-form-meta-top two">
            <label className="ci-form-field">
              <span>Paid by</span>
              <select value={method} onChange={(e) => setMethod(e.target.value)}>
                <option>Cash</option>
                <option>Transfer</option>
                <option>Card</option>
                {owedOK && <option>Not paid yet</option>}
              </select>
            </label>
          </div>
        )}
        <div className="ci-form-meta-top three">
          <label className="ci-form-field">
            <span>Amount</span>
            <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" />
          </label>
          <label className="ci-form-field">
            <span>Date</span>
            <input type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label className="ci-form-field">
            <span>{onAccount ? 'Owed to' : 'Paid to (optional)'}</span>
            <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">{onAccount ? 'Choose the vendor…' : 'No vendor'}</option>
              <CreateOption kind="vendor" />
              {vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
            </select>
          </label>
          <JobSelect value={jobId} onChange={setJobId} />
        </div>
        {onAccount && (
          <div className="ci-form-meta-top three">
            <label className="ci-form-field">
              <span>Due date</span>
              <input type="date" value={dueDate || date} min={date} onChange={(e) => setDueDate(e.target.value)} />
            </label>
            <label className="ci-form-field">
              <span>Vendor’s bill number (optional)</span>
              <input value={billNumber} onChange={(e) => setBillNumber(e.target.value)} placeholder="As on their bill" />
            </label>
          </div>
        )}
        <div className="ci-form-meta-top one">
          <label className="ci-form-field">
            <span>Description (optional)</span>
            <input value={what} onChange={(e) => setWhat(e.target.value)} placeholder="e.g. October rent" />
          </label>
        </div>
        <div className="exp-attach">
          <span className="exp-attach-label">Bill or receipt (optional)</span>
          {files.length > 0 && (
            <ul className="exp-attach-list">
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`}>
                  <Paperclip size={13} aria-hidden="true" />
                  <span>{f.name}</span>
                  <span className="exp-attach-size">{fileSize(f.size)}</span>
                  <button type="button" onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}><X size={13} /></button>
                </li>
              ))}
            </ul>
          )}
          <input ref={fileInput} type="file" accept={FILE_ACCEPT} multiple hidden onChange={(e) => pickFiles(e.target.files)} />
          <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => fileInput.current?.click()}>
            <Upload size={13} />{files.length ? 'Add another' : 'Attach a file'}
          </button>
          <span className="exp-attach-hint">PDF or photo, up to 10 MB each.</span>
        </div>
        {books && debitAcc && creditAcc && num(amount) > 0 && (
          <p className="doc-note exp-entry">
            <span>Debit {debitAcc.code} {debitAcc.name}</span><strong>{money(amount)}</strong>
            <span>Credit {creditAcc.code} {creditAcc.name}</span><strong>{money(amount)}</strong>
          </p>
        )}
        {onAccount && (
          <p className="doc-hint">
            Not paid yet: it shows as owed to {vendor?.name || 'the vendor'} under Purchase, where it’s paid like any other bill.
          </p>
        )}
        {isAdmin && books && (
          <p className="doc-hint">
            Need another account, such as Rent or Utilities? Add it in the <Link to="/master/accounts">chart of accounts</Link>.
          </p>
        )}
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !setup} onClick={save}><Receipt size={14} />{saving ? 'Saving…' : 'Record expense'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(-1)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function AccountSelect({ accounts, value, onChange, empty }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {accounts.length === 0 && <option value="">{empty}</option>}
      {groupAccounts(accounts).map(([group, list]) => (
        <optgroup key={group} label={group}>
          {list.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}{a.owed ? ' (pay later)' : ''}</option>)}
        </optgroup>
      ))}
      <CreateOption kind="account" />
    </select>
  )
}
